import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldCheck, AlertTriangle, ExternalLink, Calendar } from "lucide-react";
import { Car } from "@shared/schemas/cars";

interface SafetyInfoBoxProps {
  car: Car;
}

export function SafetyInfoBox({ car }: SafetyInfoBoxProps) {
  const hasRego = car.rego_status === "Yes";
  const regoExpired = car.rego_status === "Expired";
  const hasRWC = car.rwc_available;
  const hasVIN = !!car.vin;

  // Rego 만료일 체크
  const isRegoExpiringSoon = car.rego_expiry_date
    ? (() => {
        const expiryDate = new Date(car.rego_expiry_date);
        const today = new Date();
        const daysUntilExpiry = Math.ceil(
          (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
      })()
    : false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" />
          안전 정보
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Rego Status */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">차량 등록 (Rego)</span>
            <Badge
              variant={
                hasRego
                  ? "default"
                  : regoExpired
                  ? "destructive"
                  : "outline"
              }
            >
              {car.rego_status || "정보 없음"}
            </Badge>
          </div>
          {car.rego_expiry_date && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>만료일: {car.rego_expiry_date}</span>
              {isRegoExpiringSoon && (
                <Badge variant="outline" className="ml-2">
                  곧 만료
                </Badge>
              )}
            </div>
          )}
          {regoExpired && (
            <div className="mt-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">Rego 만료됨</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    이 차량의 Rego가 만료되었습니다. 구매 전 반드시 확인하세요.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RWC Status */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Roadworthy Certificate (RWC)</span>
            {hasRWC ? (
              <Badge variant="default">
                <ShieldCheck className="w-3 h-3 mr-1" />
                보유
              </Badge>
            ) : (
              <Badge variant="outline">정보 없음</Badge>
            )}
          </div>
          {!hasRWC && (
            <p className="text-xs text-muted-foreground">
              RWC는 차량이 호주 도로 주행 기준을 만족한다는 증명서입니다.
            </p>
          )}
        </div>

        {/* VIN */}
        {hasVIN && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">VIN 번호</span>
              <Badge variant="outline">{car.vin}</Badge>
            </div>
          </div>
        )}

        {/* PPSR Check 안내 */}
        <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
          <div className="flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="flex-1 space-y-2">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                PPSR Check 필수
              </p>
              <p className="text-xs text-blue-800 dark:text-blue-200">
                구매 전 반드시 PPSR (Personal Property Securities Register)를 확인하세요.
              </p>
              <ul className="list-disc list-inside text-xs space-y-1 text-blue-700 dark:text-blue-300">
                <li>차량에 금융이 남아있는지 확인</li>
                <li>도난 차량 여부 확인</li>
                <li>압류/저당 여부 확인</li>
              </ul>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  const vin = car.vin || "";
                  const ppsrUrl = `https://www.ppsr.gov.au/ppsr/search?searchType=Vehicle&vin=${encodeURIComponent(vin)}`;
                  window.open(ppsrUrl, "_blank");
                }}
              >
                PPSR Check 하기
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>

        {/* 안전 거래 가이드 */}
        <div className="pt-2 border-t">
          <Button
            variant="link"
            size="sm"
            className="p-0 h-auto text-xs"
            onClick={() => {
              // TODO: 안전 거래 가이드 페이지로 이동
              window.open("/safety-guide", "_blank");
            }}
          >
            안전 거래 가이드 보기
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

